/**
 * Horizontal-scroll layout renderer.
 * Makes the scene sticky when its center reaches the viewport center,
 * then translates the inner content horizontally as the user scrolls
 * vertically. When the horizontal pan completes, normal scrolling resumes.
 *
 * Config: distance (vh), imageWidth (% of container), viewport (%).
 */

import { areAnimationsEnabled } from '../../../utils/animation-settings'
import { resolveSceneBg, applyResponsiveImage } from '../../../utils/pathResolver'
import { scrollManager } from '../../../utils/scroll'
import type { SceneData } from '../types'

export interface HorizontalScrollRendererContext {
  container: HTMLDivElement
  wrapper: HTMLDivElement | null
  imageContainer: HTMLDivElement | null
  data: SceneData
  onImageContainerSet: (container: HTMLDivElement) => void
  onAppendSceneNumberDebug: () => void
  onAfterImageLoad?: () => void
}

interface HorizontalScrollConfig {
  distance: number
  imageWidth: number
  viewport: number
  unlockAfter: number
}

function getConfig(data: SceneData): HorizontalScrollConfig {
  const cfg = data.layout?.config || {}
  return {
    distance: cfg.distance ?? 3,
    imageWidth: cfg.imageWidth ?? 190,
    viewport: cfg.start?.viewport ?? 50,
    unlockAfter: cfg.unlockAfter ?? 0,
  }
}

interface HorizontalScrollDOM {
  container: HTMLDivElement
  clipContainer: HTMLDivElement
  panLayer: HTMLDivElement
  bgImg: HTMLImageElement
}

function createDOM(context: HorizontalScrollRendererContext, config: HorizontalScrollConfig): HorizontalScrollDOM {
  const { container, data, onImageContainerSet } = context

  container.style.minHeight = '100svh'
  container.style.justifyContent = 'center'
  container.style.overflow = 'hidden'

  const clipContainer = document.createElement('div')
  clipContainer.className = 'scene__image scene__image--hscroll-clip'
  clipContainer.style.overflow = 'hidden'
  clipContainer.style.width = '100%'
  clipContainer.style.position = 'relative'

  const panLayer = document.createElement('div')
  panLayer.className = 'scene__image--hscroll-pan'
  panLayer.style.width = areAnimationsEnabled() ? `${config.imageWidth}%` : '100%'
  panLayer.style.position = 'relative'
  panLayer.style.willChange = 'transform'

  onImageContainerSet(panLayer)

  const bgPath = resolveSceneBg(data) ?? ''
  const bgImg = document.createElement('img')
  bgImg.src = bgPath
  bgImg.alt = data.id
  bgImg.className = 'scene__img'
  bgImg.style.width = '100%'
  bgImg.style.height = 'auto'
  bgImg.style.display = 'block'
  bgImg.style.maxWidth = 'none'
  applyResponsiveImage(bgImg)

  panLayer.appendChild(bgImg)
  clipContainer.appendChild(panLayer)
  container.appendChild(clipContainer)

  return { container, clipContainer, panLayer, bgImg }
}

export function renderHorizontalScroll(context: HorizontalScrollRendererContext): HTMLElement {
  const { data } = context
  const config = getConfig(data)
  const dom = createDOM(context, config)

  const runAfterLoad = () => {
    context.onAfterImageLoad?.()

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setupHorizontalScrollBehavior(context.wrapper, dom, data, config)
        context.onAppendSceneNumberDebug()
      })
    })
  }

  if (dom.bgImg.complete) runAfterLoad()
  else dom.bgImg.addEventListener('load', runAfterLoad, { once: true })

  return dom.container
}

/**
 * Constrain the clip container width so the image height never exceeds
 * the viewport height. This ensures the full image is visible when sticky.
 */
function applyViewportFit(
  clipContainer: HTMLDivElement,
  bgImg: HTMLImageElement,
  config: HorizontalScrollConfig,
): void {
  if (bgImg.naturalWidth <= 0 || bgImg.naturalHeight <= 0) return
  const vh = window.innerHeight
  const imageAspect = bgImg.naturalWidth / bgImg.naturalHeight
  const maxClipWidth = (vh * imageAspect * 100) / config.imageWidth
  clipContainer.style.maxWidth = `${maxClipWidth}px`
}

function setupHorizontalScrollBehavior(
  wrapper: HTMLDivElement | null,
  dom: HorizontalScrollDOM,
  data: SceneData,
  config: HorizontalScrollConfig,
): void {
  if (!wrapper) return

  const { panLayer, clipContainer, bgImg } = dom

  if (!areAnimationsEnabled()) return

  const vh = window.innerHeight
  const stickyDistance = vh * config.distance

  const stickyContainer = document.createElement('div')
  stickyContainer.className = 'hscroll-sticky-container'
  stickyContainer.style.width = '100%'
  stickyContainer.style.position = 'relative'

  const setupSticky = () => {
    const parentElement = wrapper.parentElement
    if (!parentElement) {
      requestAnimationFrame(setupSticky)
      return
    }

    parentElement.insertBefore(stickyContainer, wrapper)
    stickyContainer.appendChild(wrapper)

    wrapper.style.position = 'sticky'
    wrapper.style.top = '0px'
    wrapper.style.zIndex = '1'
    applyViewportFit(clipContainer, bgImg, config)

    const updateDimensions = () => {
      applyViewportFit(clipContainer, bgImg, config)
      const wrapperHeight = wrapper.getBoundingClientRect().height || wrapper.offsetHeight
      if (wrapperHeight > 0) {
        stickyContainer.style.height = `${wrapperHeight + stickyDistance}px`
      }
    }

    if (bgImg.complete) updateDimensions()
    else bgImg.addEventListener('load', updateDimensions, { once: true })

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateDimensions()
        setTimeout(updateDimensions, 200)
      })
    })

    const getStickyStartScroll = (): number => {
      const containerRect = stickyContainer.getBoundingClientRect()
      return containerRect.top + window.scrollY
    }

    let panLocked = config.unlockAfter > 0

    const updateScrollProgress = () => {
      if (panLocked) {
        panLayer.style.transform = 'translateX(0px)'
        return
      }
      const scrollStartY = getStickyStartScroll()
      const relativeScroll = window.scrollY - scrollStartY
      const progress = Math.max(0, Math.min(1, relativeScroll / stickyDistance))

      const panWidth = panLayer.scrollWidth || panLayer.offsetWidth
      const clipWidth = clipContainer.offsetWidth
      const maxTranslate = Math.max(0, panWidth - clipWidth)

      panLayer.style.transform = `translateX(${-progress * maxTranslate}px)`
    }

    let scrollRafId: number | null = null
    const scrollHandler = () => {
      if (scrollRafId === null) {
        scrollRafId = requestAnimationFrame(() => {
          updateScrollProgress()
          scrollRafId = null
        })
      }
    }
    window.addEventListener('scroll', scrollHandler, { passive: true })

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions()
      updateScrollProgress()
    })
    resizeObserver.observe(wrapper)
    resizeObserver.observe(stickyContainer)
    requestAnimationFrame(() => updateScrollProgress())
    ;(wrapper as any).__horizontalScrollCleanup = () => {
      if (scrollRafId !== null) cancelAnimationFrame(scrollRafId)
      window.removeEventListener('scroll', scrollHandler)
      resizeObserver.disconnect()
      if (stickyContainer.parentElement && wrapper.parentElement === stickyContainer) {
        stickyContainer.parentElement.insertBefore(wrapper, stickyContainer)
      }
      if (stickyContainer.parentElement) {
        stickyContainer.parentElement.removeChild(stickyContainer)
      }
      wrapper.style.position = ''
      wrapper.style.top = ''
      wrapper.style.zIndex = ''
    }

    if (config.unlockAfter > 0) {
      let locked = true

      const applyPhysicalLock = () => {
        if (!locked) return
        const lockY = getStickyStartScroll()
        scrollManager.setPriorityLock(lockY)
      }

      const isAtLockBoundary = () => {
        const lockY = getStickyStartScroll()
        const lenis = (window as any).__lenis
        const current = lenis?.targetScroll ?? lenis?.scroll ?? window.scrollY
        return current >= lockY - 2
      }

      const blockWheel = (e: WheelEvent) => {
        if (!locked) return
        if (e.deltaY > 0 && isAtLockBoundary()) {
          e.preventDefault()
          e.stopPropagation()
        }
      }

      let touchStartY = 0
      const blockTouchStart = (e: TouchEvent) => {
        touchStartY = e.touches[0]?.clientY ?? 0
      }
      const blockTouchMove = (e: TouchEvent) => {
        if (!locked) return
        const dy = touchStartY - (e.touches[0]?.clientY ?? 0)
        if (dy > 0 && isAtLockBoundary()) {
          e.preventDefault()
          e.stopPropagation()
        }
      }

      let lockRafPending = false
      const lockScrollHandler = () => {
        if (!locked || lockRafPending) return
        lockRafPending = true
        requestAnimationFrame(() => {
          lockRafPending = false
          if (locked) applyPhysicalLock()
        })
      }

      window.addEventListener('wheel', blockWheel, { passive: false, capture: true })
      window.addEventListener('touchstart', blockTouchStart, { passive: true, capture: true })
      window.addEventListener('touchmove', blockTouchMove, { passive: false, capture: true })
      window.addEventListener('scroll', lockScrollHandler, { passive: true })

      applyPhysicalLock()

      ;(wrapper as any).__horizontalScrollUnlock = () => {
        if (!locked) return
        locked = false
        panLocked = false
        scrollManager.clearPriorityLock()
        window.removeEventListener('wheel', blockWheel, { capture: true })
        window.removeEventListener('touchstart', blockTouchStart, { capture: true })
        window.removeEventListener('touchmove', blockTouchMove, { capture: true })
        window.removeEventListener('scroll', lockScrollHandler)
      }

      const origCleanup = (wrapper as any).__horizontalScrollCleanup
      ;(wrapper as any).__horizontalScrollCleanup = () => {
        ;(wrapper as any).__horizontalScrollUnlock?.()
        origCleanup?.()
      }
    }
  }

  setupSticky()

  if (window.matchMedia('(pointer: coarse)').matches) {
    setupHorizontalTouchScroll(wrapper, dom, config)
  }
}

/**
 * On touch-primary devices, allow horizontal swipe anywhere in the scene
 * to drive vertical scrolling (right swipe = scroll up, left swipe = scroll down).
 */
function setupHorizontalTouchScroll(
  wrapper: HTMLDivElement | null,
  _dom: HorizontalScrollDOM,
  _config: HorizontalScrollConfig,
): void {
  if (!wrapper) return

  const GESTURE_THRESHOLD = 10

  let lastTouchX = 0
  let touchStartX = 0
  let touchStartY = 0
  let isHorizontalGesture = false
  let gestureDecided = false

  const onTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0]
    touchStartX = touch.clientX
    touchStartY = touch.clientY
    lastTouchX = touch.clientX
    isHorizontalGesture = false
    gestureDecided = false
  }

  const onTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0]
    const currentX = touch.clientX
    const currentY = touch.clientY

    if (!gestureDecided) {
      const absX = Math.abs(currentX - touchStartX)
      const absY = Math.abs(currentY - touchStartY)
      if (absX > GESTURE_THRESHOLD || absY > GESTURE_THRESHOLD) {
        isHorizontalGesture = absX > absY
        gestureDecided = true
      }
      if (!gestureDecided) return
    }

    if (!isHorizontalGesture) return

    e.preventDefault()

    const frameDelta = currentX - lastTouchX
    lastTouchX = currentX

    const lenis = (window as any).__lenis
    if (lenis) {
      lenis.scrollTo(lenis.scroll - frameDelta, { immediate: true })
    } else {
      window.scrollBy(0, -frameDelta)
    }
  }

  wrapper.addEventListener('touchstart', onTouchStart, { passive: true })
  wrapper.addEventListener('touchmove', onTouchMove, { passive: false })

  const origCleanup = (wrapper as any).__horizontalScrollCleanup
  ;(wrapper as any).__horizontalScrollCleanup = () => {
    wrapper.removeEventListener('touchstart', onTouchStart)
    wrapper.removeEventListener('touchmove', onTouchMove)
    origCleanup?.()
  }
}
